import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Building2, Mail, MapPin, Phone, ShieldCheck, Sparkles } from "lucide-react";

import { CenterMembershipView } from "../../../center";

interface Props {
    membership: CenterMembershipView;
}

/**
 * The centre an account belongs to, as its own member sees it.
 *
 * Who the centre is, and what this account is in it. The role is spelled out
 * because an account can hold both hats, and the words differ by vertical —
 * a training centre has trainers, not therapists.
 */
export default function MyCenterCard({ membership }: Props) {
    const { t } = useTranslation();
    const { center } = membership;

    const roleLabels = membership.roles.map((role) =>
        role === "admin"
            ? t("center.roles.admin")
            : center.type === "training"
              ? t("center.roles.trainer")
              : t("center.roles.therapist")
    );

    const details = [
        { icon: MapPin, value: center.address },
        { icon: Mail, value: center.contact_email },
        { icon: Phone, value: center.contact_phone },
    ].filter((detail) => Boolean(detail.value));

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="flex items-start gap-4">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
                    {center.logo_url ? (
                        <img src={center.logo_url} alt="" className="h-full w-full rounded-xl object-cover" />
                    ) : (
                        <Building2 size={22} aria-hidden="true" />
                    )}
                </span>
                <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-content-primary">{center.name}</h3>
                    <p className="text-sm text-content-tertiary">{t(`center.types.${center.type}`)}</p>
                    {center.legal_name && (
                        <p className="mt-0.5 text-xs text-content-tertiary">{center.legal_name}</p>
                    )}
                </div>
            </div>

            <div className="mt-6">
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-content-tertiary">
                    {t("center.profile.yourRole")}
                </p>
                <div className="flex flex-wrap gap-2">
                    {roleLabels.map((label) => (
                        <span
                            key={label}
                            className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-sm font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-400"
                        >
                            <ShieldCheck size={14} aria-hidden="true" />
                            {label}
                        </span>
                    ))}
                </div>
            </div>

            {details.length > 0 && (
                <ul className="mt-6 space-y-2 border-t border-gray-100 pt-5 text-sm text-content-secondary dark:border-gray-800">
                    {details.map(({ icon: Icon, value }) => (
                        <li key={value} className="flex items-center gap-2">
                            <Icon size={16} className="text-content-tertiary" aria-hidden="true" />
                            {value}
                        </li>
                    ))}
                </ul>
            )}

            <p className="mt-6 flex items-center gap-2 rounded-xl bg-gray-50 px-4 py-3 text-sm text-content-secondary dark:bg-white/[0.02]">
                <Sparkles size={16} className="shrink-0 text-brand-500" aria-hidden="true" />
                {t("center.profile.seatPlan")}
            </p>

            {membership.is_admin && (
                <Link
                    to="/center"
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline dark:text-brand-400"
                >
                    {t("center.profile.goToAdmin")}
                </Link>
            )}
        </section>
    );
}
